import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarPlus, FaTimes, FaDumbbell, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { scheduleWorkout } from '../../services/fitnessService';
import toast from 'react-hot-toast';
import './ScheduleModal.css';

// workouts prop: for programs, pass program.workouts array [{dayNumber, dayName, workoutDetails: {workoutId, name, ...}}, ...]
// durationWeeks prop: total weeks the program runs (e.g. 4). Week 1 pattern is replicated.
const ScheduleModal = ({ type, itemId, itemName, workouts, durationWeeks = 1, onClose, onScheduled }) => {
  const navigate = useNavigate();
  const [scheduledDate, setScheduledDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null); // { count: number }

  // For program mode: each workout gets its own date
  const programWorkouts = (workouts || []).filter(w => !w.isRestDay && w.workoutDetails);
  const totalWeeks = Math.max(1, durationWeeks || 1);

  // Build full schedule: replicate week 1 pattern for all weeks
  // Each entry: { weekNum, dayNumber, workout, key }
  const fullSchedule = [];
  for (let week = 0; week < totalWeeks; week++) {
    programWorkouts.forEach(w => {
      fullSchedule.push({
        weekNum: week + 1,
        dayNumber: w.dayNumber,
        workout: w,
        key: `${week}-day${w.dayNumber}`
      });
    });
  }

  // State: map of key -> date string
  const [workoutDates, setWorkoutDates] = useState({});

  const minDate = new Date().toISOString().split('T')[0];
  const isProgram = type === 'program' && programWorkouts.length > 0;
  const programMissingWorkouts = type === 'program' && programWorkouts.length === 0;

  const setWorkoutDate = (key, date) => {
    setWorkoutDates(prev => ({ ...prev, [key]: date }));
  };

  // Auto-fill: from a start date, assign dates based on dayNumber offsets, replicated per week
  const autoFillDates = (startDate) => {
    const dates = {};
    const start = new Date(startDate + 'T12:00:00');
    // Calculate day offsets from the first workout's dayNumber
    const minDay = Math.min(...programWorkouts.map(w => w.dayNumber || 1));
    
    for (let week = 0; week < totalWeeks; week++) {
      programWorkouts.forEach(w => {
        const dayOffset = (w.dayNumber || 1) - minDay;
        const d = new Date(start);
        d.setDate(d.getDate() + (week * 7) + dayOffset);
        dates[`${week}-day${w.dayNumber}`] = d.toISOString().split('T')[0];
      });
    }
    setWorkoutDates(dates);
  };

  const handleSubmitWorkout = async (e) => {
    e.preventDefault();
    if (!scheduledDate) return;

    setSubmitting(true);
    setError(null);
    setLimitInfo(null);

    try {
      await scheduleWorkout({
        type: 'workout',
        workoutId: itemId,
        scheduledDate,
        notes: notes.trim() || undefined
      });
      toast.success('Workout scheduled!');
      if (onScheduled) onScheduled();
      setSuccessInfo({ count: 1 });
    } catch (err) {
      if (err.data?.upgrade) {
        setLimitInfo({ currentCount: err.data.currentCount, limit: err.data.limit });
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitProgram = async (e) => {
    e.preventDefault();
    const toSchedule = fullSchedule.filter(entry => workoutDates[entry.key]);
    if (toSchedule.length === 0) return;

    setSubmitting(true);
    setError(null);
    setLimitInfo(null);

    let scheduled = 0;
    for (const entry of toSchedule) {
      try {
        await scheduleWorkout({
          type: 'workout',
          workoutId: entry.workout.workoutDetails.workoutId,
          scheduledDate: workoutDates[entry.key],
          notes: notes.trim() || undefined
        });
        scheduled++;
      } catch (err) {
        if (err.data?.upgrade) {
          setLimitInfo({ currentCount: err.data.currentCount, limit: err.data.limit });
          break;
        } else {
          setError(err.message);
          break;
        }
      }
    }

    if (scheduled > 0) {
      toast.success(`${scheduled} workout${scheduled > 1 ? 's' : ''} scheduled!`);
      if (onScheduled) onScheduled();
      setSuccessInfo({ count: scheduled });
    }
    if (error || limitInfo) {
      // stay on form so user sees the error
    }
    setSubmitting(false);
  };

  const assignedCount = isProgram
    ? Object.values(workoutDates).filter(Boolean).length
    : 0;

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className={`schedule-modal ${isProgram ? 'schedule-modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>

        {successInfo ? (
          /* SUCCESS STATE */
          <div className="schedule-success">
            <FaCheckCircle className="schedule-success-icon" />
            <h3>Scheduled!</h3>
            <p>
              {successInfo.count === 1
                ? `${itemName} has been added to your schedule.`
                : `${successInfo.count} workouts have been added to your schedule.`}
            </p>
            <div className="schedule-success-actions">
              <button
                className="schedule-success-primary"
                onClick={() => { onClose(); navigate('/fitness/my-workouts'); }}
              >
                View My Workouts <FaArrowRight />
              </button>
              <button className="schedule-success-secondary" onClick={onClose}>
                Stay Here
              </button>
            </div>
          </div>
        ) : (
          /* FORM STATE */
          <>
        <div className="schedule-modal-header">
          <h3>
            <FaCalendarPlus />{' '}
            {isProgram ? 'Schedule program workouts' : programMissingWorkouts ? 'Schedule program' : 'Schedule workout'}
          </h3>
          <button className="schedule-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="schedule-modal-name" title={itemName}>{itemName}</div>

        {limitInfo && (
          <div className="schedule-limit-warning">
            <i className="fas fa-exclamation-triangle" />
            <div>
              You've reached your weekly schedule limit ({limitInfo.currentCount}/{limitInfo.limit}).
              Upgrade your plan for more.
            </div>
          </div>
        )}

        {error && <div className="schedule-error">{error}</div>}

        {programMissingWorkouts ? (
          <div className="schedule-program-empty">
            <p>
              This program doesn&apos;t have workout days linked yet, so nothing can be added to your calendar.
              Add workouts in the program editor, then schedule again.
            </p>
            <div className="schedule-modal-actions">
              <button type="button" className="schedule-submit-btn" onClick={onClose}>
                OK
              </button>
            </div>
          </div>
        ) : isProgram ? (
          /* PROGRAM MODE: assign dates to each workout */
          <form onSubmit={handleSubmitProgram}>
            <div className="schedule-form-group">
              <label>Pick a start date — workouts fill automatically for {totalWeeks} week{totalWeeks > 1 ? 's' : ''}</label>
              <input
                type="date"
                min={minDate}
                onChange={(e) => e.target.value && autoFillDates(e.target.value)}
              />
            </div>

            <div className="program-workout-dates">
              {Array.from({ length: totalWeeks }, (_, week) => {
                const weekEntries = fullSchedule.filter(e => e.weekNum === week + 1);
                return (
                  <div key={week} className="program-week-group">
                    <div className="program-week-label">Week {week + 1}</div>
                    {weekEntries.map(entry => (
                      <div key={entry.key} className="program-workout-row">
                        <div className="program-workout-info">
                          <FaDumbbell className="program-workout-icon" />
                          <div>
                            <span className="program-workout-day">Day {entry.dayNumber}</span>
                            <span className="program-workout-name" title={entry.workout.workoutDetails.name}>{entry.workout.workoutDetails.name}</span>
                          </div>
                        </div>
                        <input
                          type="date"
                          className="program-workout-date"
                          min={minDate}
                          value={workoutDates[entry.key] || ''}
                          onChange={(e) => setWorkoutDate(entry.key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="schedule-form-group">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Week 1 of program..."
                rows={2}
              />
            </div>

            <div className="schedule-modal-actions">
              <button type="button" className="schedule-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="schedule-submit-btn"
                disabled={submitting || assignedCount === 0}
              >
                <FaCalendarPlus />
                {submitting ? 'Scheduling...' : `Schedule ${assignedCount} Workout${assignedCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        ) : (
          /* WORKOUT MODE: single date picker */
          <form onSubmit={handleSubmitWorkout}>
            <div className="schedule-form-group">
              <label>Date</label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={minDate}
                required
              />
            </div>

            <div className="schedule-form-group">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Morning session, focus on form..."
                rows={2}
              />
            </div>

            <div className="schedule-modal-actions">
              <button type="button" className="schedule-cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="schedule-submit-btn"
                disabled={submitting || !scheduledDate}
              >
                <FaCalendarPlus />
                {submitting ? 'Scheduling...' : 'Schedule'}
              </button>
            </div>
          </form>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleModal;
